import Tooltip from '@material-ui/core/Tooltip';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ReactComponent as CommentIcon } from '@assets/icons/order/Comment-not-read.svg';
import { ReactComponent as SlideIcon } from '@assets/icons/order/slide-icon.svg';

import LoaderComponent from '@components/LoaderComponent';
import CommonImage from '@components/CommonImage';

import AllTask, { HANDOVER_TO_FORWARDER_STEP } from '@features/order-dashboard/component/AllTask';
import CompleteOnPastDateModal from '@features/order-dashboard/component/CompleteOnPastDateModal';
import ExFactorySupplierQuote from '@features/order-dashboard/component/ExFactorySupplierQuote';
import InspectionDetailsModal from '@features/order-dashboard/component/InspectionDetailsModal';
import OrderSummary from '@features/order-dashboard/component/OrderSummary';
import {
	BrandAndTeamCell,
	BuyerEtdCell,
	ExFactoryCell,
	FabricCell,
	FactoryEtdCell,
	InspectionDetailsCell,
	PoCell,
	QuotationBuyerPriceCell,
	QuotationFactoryPriceCell,
	ScCell,
	ShipmentCell,
	StyleCell
} from '@features/order-dashboard/component/OrderDashboardTableCells';
import OrderRemarks from '@features/order-dashboard/component/OrderRemarks';

import { setCurrentActivePage } from '@store/actions/ActivePageAction';
import { storeNewMsg } from '@store/actions/ChatAction';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchEnumValues } from '@services/commonService';
import tnaService from '@services/tna-service';
import { completeOrderStepByIdList } from '@services/orderDashboard';

import { formatNumberWithSuffix } from '@utils/helper';
import {
	_getKey,
	capitalizeFirstLetter,
	changeDateFormat,
	getShortName,
	printApiError,
	STATIC_SET_CONSTANTS
} from '@utils/Util';

const filterBy = ['Order placed', 'Inspection', 'Delivery'];

// How close to its planned date a task has to be before its cell is tinted amber.
const DUE_SOON_DAYS = 5;

const OrderDashboard = ({
	orderStore,
	callback,
	showSummary,
	requestFrom = 'ORDER_DASHBOARD',
	handleOrderDashboardScroll,
	isShowTasks = true,
	isShowRemarks = true,
	queryParams,
	updateOrderStepByOrders
}) => {
	const [loader] = useState(false);
	const [, setColors] = useState({});
	const [showAllTask, setShowAllTask] = useState(false);
	const [selectedItem, setSelectedItem] = useState({});
	const [hiddenUnsafeTasks, setHiddenUnsafeTasks] = useState([]);
	const [orderDashboardData, setOrderDashboardData] = useState([]);
	const [placeOrderDeleveryInspactionColumn, setPlaceOrderDeleveryInspactionColumn] = useState([]);
	const orderDashboardStore = useSelector((store) => store.orderDashboardStore);
	const filterFields = useSelector((store) => store.filterFieldStore);
	const location = useLocation();
	const dispatch = useDispatch();
	const elementRef = useRef(null);
	const [arrowDisable, setArrowDisable] = useState(true);
	const newMessages = useSelector((store) => store.chatReducer.newMessage);
	const [deliverableList, setDeliverableList] = useState([]);
	const [isTnaStatusModalHasSet] = useState(false);
	const [streamList, setStreamList] = useState([]);
	const [groupByCount, setGroupByCount] = useState([]);
	const [selectedGroupBy, setSelectedGroupBy] = useState(null);
	const [activePage, setActivePage] = useState(null);
	const [groupWiseParams, setGroupWiseParams] = useState('');
	// The tasks ticked for completion, each kept with the style it belongs to: handing over to the
	// forwarder is recorded against that style's quote, so the id alone would not be enough.
	const [selectedTasks, setSelectedTasks] = useState([]);
	const [handoverTask, setHandoverTask] = useState(null);
	// The back date a selection is being completed on, held while the handover forms are
	// answered so the whole selection still lands on the day the user picked.
	const [completionDate, setCompletionDate] = useState(null);
	const [showPastDateModal, setShowPastDateModal] = useState(false);
	const [pendingHandoverTasks, setPendingHandoverTasks] = useState([]);
	const [showInspectionDetails, setShowInspectionDetails] = useState(false);
	const [selectedInspectionItem, setSelectedInspectionItem] = useState(null);

	const asSelectedTask = (currentStep, style, rfqId) => ({
		id: currentStep?.id,
		stepName: currentStep?.stepName,
		rfqId: rfqId ?? style?.id,
		factoryName: style?.factoryName,
		factoryId: style?.factoryId,
		// How far back this task may be completed; see latestHandoverDate.
		factoryHandoverDate: style?.factoryHandoverDate
	});

	const isTaskSelected = (taskId) => selectedTasks.some((task) => task.id === taskId);

	// Nothing on a TNA happens before its order reaches the factory, so a selection spanning
	// several orders can only go back as far as the latest handover among them. ISO dates
	// compare as strings.
	const latestHandoverDate = selectedTasks
		.map((task) => task.factoryHandoverDate)
		.filter(Boolean)
		.reduce((latest, date) => (latest === null || date > latest ? date : latest), null);

	const selectTasks = (tasks) =>
		setSelectedTasks((current) => [
			...current,
			...tasks.filter((task) => !current.some((selected) => selected.id === task.id))
		]);

	const deselectTasks = (tasks) =>
		setSelectedTasks((current) =>
			current.filter((selected) => !tasks.some((task) => task.id === selected.id))
		);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const queryParam = params.get('active');
		if (queryParam) {
			setActivePage('ORDER_DASHBOARD');
		}
	}, [location.search]);

	useEffect(() => {
		setGroupWiseParams(queryParams);
	}, [queryParams]);

	const getGroupWiseCount = async (groupWiseParams) => {
		const sort = filterFields?.sort || [];
		let params = '';

		sort.forEach((item) => {
			if (params.length > 0) {
				params += '&';
			}
			if (item.value === 'Group by order') {
				params += `groupBy=ORDER`;
			} else if (item.value === 'Group by brand') {
				params += `groupBy=BRAND`;
			} else if (item.value === 'Group by factory') {
				params += `groupBy=FACTORY`;
			} else if (item.value === 'Group by brand & factory') {
				params += `groupBy=BRAND_FACTORY`;
			}
		});
		params += groupWiseParams;

		const ordersList = await tnaService.getGroupWiseOrderCount(`?${params}`);
		setGroupByCount(Array.isArray(ordersList) ? ordersList : []);
	};

	useEffect(() => {
		const sort = filterFields?.sort || [];
		sort.forEach((item) => {
			if (item.value === 'Group by order') {
				setSelectedGroupBy('ORDER');
			} else if (item.value === 'Group by brand') {
				setSelectedGroupBy('BRAND');
			} else if (item.value === 'Group by factory') {
				setSelectedGroupBy('FACTORY');
			} else if (item.value === 'Group by brand & factory') {
				setSelectedGroupBy('BRAND_FACTORY');
			} else {
				setSelectedGroupBy('Ungrouped');
			}
		});
	}, [filterFields]);

	useEffect(() => {
		(async () => {
			if (requestFrom === 'ORDER_DASHBOARD' && groupWiseParams)
				await getGroupWiseCount(groupWiseParams);
		})();
	}, [groupWiseParams, requestFrom]);

	useEffect(() => {
		(async () => {
			try {
				await fetchEnumValues('info.nitex.app.common.enums.OrderStream', setStreamList);
			} catch {
				/* Empty Block */
			}
		})();
	}, []);

	useEffect(() => {
		setCurrentActivePage({
			id: null,
			type: 'ORDER_DASHBOARD_UNREAD_POST_DELIVERABLE',
			pageType: 'DETAILS'
		});
	}, []);

	useEffect(() => {
		// Columns are tasks now, so the trailing group is picked out by the deliverable each
		// task belongs to rather than by the column's own name.
		const belongsToTrailingGroup = (order) => filterBy.includes(order.deliverableName ?? order.name);
		const orderDashboardList = orderDashboardStore?.headings.filter(
			(order) => !belongsToTrailingGroup(order)
		);
		const extractColumns = orderDashboardStore?.headings.filter(belongsToTrailingGroup);
		setPlaceOrderDeleveryInspactionColumn(extractColumns);
		setOrderDashboardData(orderDashboardList);
		// Split again whenever the headings change, not only when there are more or fewer of
		// them: opening an order re-reads them under that buyer's own task names, which leaves
		// the count the same and the names different.
	}, [orderDashboardStore.headings]);

	useEffect(() => {
		if (Array.isArray(newMessages)) {
			setDeliverableList(newMessages);
		}
	}, [newMessages]);

	const toggleTaskMessageRead = (id) => {
		let tmpList = deliverableList.filter((item) => parseInt(item) !== parseInt(id));
		setDeliverableList(tmpList);
		dispatch(storeNewMsg(tmpList));
	};

	useEffect(() => {
		let uniqueOrderIds = [...new Map(orderStore.map((item) => [item['orderId'], item])).values()];
		let orderObj = {};
		let colorNumber = 1;
		for (let i = 0; i <= uniqueOrderIds.length; i++) {
			if (colorNumber === 6) {
				colorNumber = 1;
			}
			if (uniqueOrderIds[i]?.orderId) {
				orderObj[uniqueOrderIds[i].orderId] = `color-${colorNumber}`;
			}
			colorNumber++;
		}
		setColors(orderObj);
	}, [orderStore]);

	// Every task of a column, across the styles on screen, as the selection carries them. Tasks
	// already approved or completed are left out: there is nothing to complete about them.
	const selectableTasksOfColumn = (item, isInspection = false) => {
		const tasks = [];
		const addTask = (tnaResponse, style) => {
			const stepInfo = findTaskForColumn(tnaResponse, item);
			if (stepInfo && !['APPROVED', 'COMPLETED'].includes(stepInfo.currentStep?.status)) {
				tasks.push(asSelectedTask(stepInfo.currentStep, style));
			}
		};
		orderStore.forEach((order) => {
			if (order.setPackStyleFlag === 'SET_STYLE' && !isInspection) {
				order.tnaSetMemberResponseList?.forEach((tnaResponse) => addTask(tnaResponse, order));
			} else {
				addTask(order.tnaResponse, order);
			}
		});
		return tasks;
	};

	const renderAllCurrentStepCheckbox = (item, isInspection = false) => {
		if (requestFrom !== 'ORDER_DETAILS') return;

		const columnTasks = selectableTasksOfColumn(item, isInspection);
		if (columnTasks.length === 0) return;

		// The whole column is ticked only when every one of its tasks is in the selection, so it
		// reads true alongside tasks picked one by one from other columns.
		const isWholeColumnSelected = columnTasks.every((task) => isTaskSelected(task.id));

		// Shown on hover like the one on a task cell, and kept in sight once the whole column
		// is ticked, so the selection stays visible as the pointer moves away.
		return (
			<div className={`custom-chekbox${isWholeColumnSelected ? ' selected' : ''}`}>
				<div className="form-group mb-0">
					<input
						type="checkbox"
						name={`${item.name}`}
						id={`${item.name}`}
						value={item.name}
						onChange={(e) => {
							e.stopPropagation();
							if (isWholeColumnSelected) deselectTasks(columnTasks);
							else selectTasks(columnTasks);
						}}
						checked={isWholeColumnSelected}
					/>
					<label htmlFor={`${item.name}`} />
				</div>
			</div>
		);
	};

	// The buyer task names that apply to a group of rows, or null where none do. Grouping by
	// brand gives one brand per group, and grouping by order does too since an order belongs
	// to one buyer; a group of several brands, or none at all, keeps the stored names. The
	// key stands in for the brand so the naming rules stay in one place, on the server.
	const groupBrandAliasKey = (items) => {
		const rows = items || [];
		if (rows.length === 0) return null;

		const brands = new Set(rows.map((row) => row?.brandName));
		return brands.size === 1 ? rows[0]?.brandAliasKey || null : null;
	};

	// A task the brand does not rename is absent from brandWiseNames and keeps its own name.
	const taskColumnName = (item, aliasKey) => (aliasKey && item?.brandWiseNames?.[aliasKey]) || item?.name;

	const renderTableHeading = (items) => {
		const aliasKey = groupBrandAliasKey(items);

		return orderDashboardData.map((item, index) => {
			if (
				orderDashboardStore.isAllColumnVisible ||
				(orderDashboardStore.isCriticalColumnVisible && item.isCritical) ||
				(orderDashboardStore.isSweaterColumnVisible && item.applicableFabric === 'SWEATER') ||
				(orderDashboardStore.isProductionColumnVisible && item?.stageConstants === 'PRODUCTION') ||
				(orderDashboardStore.isSampleColumnVisible && item?.stageConstants === 'SAMPLING') ||
				item.isColumnVisible
			) {
				const columnName = taskColumnName(item, aliasKey);
				return (
					<th key={`table_head_${index}`}>
						<div className="d-flex align-items-center align-self-center">
							{renderAllCurrentStepCheckbox(item)}
							<Tooltip title={columnName} placement={'top'} arrow>
								<span className="">{getShortName(columnName, 16)}</span>
							</Tooltip>
						</div>
					</th>
				);
			}
		});
	};

	const isExist = (masterId, idList) => {
		return idList.includes(masterId);
	};

	const handleCellClick = (taskInfo) => {
		toggleTaskMessageRead(taskInfo.id);
	};

	// Any task can be picked alongside any other, whichever column it sits in, so a style's work
	// can be signed off in one go rather than a column at a time.
	const renderCheckbox = (stepInfo, style, rfqId) => {
		const { id } = stepInfo?.currentStep;
		const task = asSelectedTask(stepInfo.currentStep, style, rfqId);
		return (
			<div
				className="custom-chekbox"
				onClick={(e) => {
					e.stopPropagation();
				}}
			>
				<div className="form-group mb-1">
					<input
						type="checkbox"
						id={`${stepInfo.name}_${id}`}
						name={`${stepInfo.name}_${id}`}
						value={id}
						checked={isTaskSelected(id)}
						onChange={() => {
							if (isTaskSelected(id)) deselectTasks([task]);
							else selectTasks([task]);
						}}
					/>
					<label htmlFor={`${stepInfo.name}_${id}`} />
				</div>
			</div>
		);
	};

	// Each column is one TNA task, so its cell shows that task's own status rather than
	// whichever task its deliverable happens to be on. The task is carried inside the
	// deliverable it belongs to, and matched on the step master behind the column.
	const findTaskForColumn = (tnaStatus, item) => {
		if (!tnaStatus) return undefined;
		for (const deliverable of tnaStatus) {
			const task = deliverable?.taskList?.find((step) =>
				isExist(step.stepMasterId, item.stepMasterIdList)
			);
			if (task) return { ...deliverable, currentStep: task };
		}
		return undefined;
	};

	// Within five days of the planned date and still open. The plan date is the P the cell
	// already prints, and whole days are counted from midnight so a task due tomorrow does
	// not turn amber only once the hour has passed.
	const isDueSoon = (currentStep) => {
		if (['APPROVED', 'COMPLETED'].includes(currentStep?.status)) return false;
		if (!currentStep?.endDate) return false;

		const daysUntilDue = moment(currentStep.endDate, 'YYYY-MM-DD')
			.startOf('day')
			.diff(moment().startOf('day'), 'days');
		return daysUntilDue >= 0 && daysUntilDue <= DUE_SOON_DAYS;
	};

	// Past its date is red, nearly due is amber, and overdue wins where both could apply.
	// Everything further out, or already signed off, is left plain. UNSAFE is the backend's
	// own word for a task whose planned date has gone by.
	const cellStatusClass = (currentStep) => {
		if (currentStep?.tnaStatus === 'UNSAFE') return 'red-alert';
		if (isDueSoon(currentStep)) return 'due-soon';
		return '';
	};

	const renderStepsTableDetails = (item, tnaStatus, style, rowSpan = 1, rfqId) => {
		let stepInfo = findTaskForColumn(tnaStatus, item);
		if (stepInfo === undefined) {
			return (
				<td className="regular-12 gray_dark_02" key={`steps_${_getKey()}`} rowSpan={rowSpan}>
					<p className="regular-12 gray_dark_01">N/A</p>
				</td>
			);
		} else {
			return (
				<td
					className={`regular-12 cursor-pointer gray_dark_02 ${cellStatusClass(
						stepInfo?.currentStep
					)} ${isTaskSelected(stepInfo?.currentStep?.id) ? 'active' : ''}`}
					key={`steps_${_getKey()}`}
					onClick={() => handleCellClick(stepInfo)}
					rowSpan={rowSpan}
				>
					{/* Delivery tasks are selectable too: handing over to the forwarder is one of
					    them, and it has to be completable with the rest of a style's work. */}
					{!['APPROVED', 'COMPLETED'].includes(stepInfo?.currentStep?.status) &&
						renderCheckbox(stepInfo, style, rfqId)}
					{!stepInfo?.currentStep?.productionTarget ? (
						<div className="d-flex align-items-center">
							{['APPROVED', 'COMPLETED'].includes(stepInfo?.currentStep?.status) && (
								<span>
									<CommonImage
										src="/icons/tick24.svg"
										alt="down"
										className="mr-1"
										width={18}
									/>
								</span>
							)}
							{/* The column heading already names the task, so the cell no longer repeats its
							    submission/approval type - only the tick, the dates and the quantities. */}
						</div>
					) : (
						<div className="production-qty">
							<span className="semibold-12">
								{stepInfo?.currentStep?.actualProductionQuantity || 0}
							</span>
							/{stepInfo?.currentStep?.productionTarget}
						</div>
					)}

					{(stepInfo?.currentStep?.formattedTaskStatus === 'LATE_APPROVED' ||
						stepInfo?.currentStep?.formattedTaskStatus === 'APPROVED' ||
						stepInfo?.currentStep?.formattedTaskStatus === 'COMPLETED') && (
						<p className="regular-12 green">
							{/* A for the date it was actually approved, against the P of the planned date
							    below it, which is how the downloaded report reads them too. */}
							{stepInfo?.currentStep?.actualEndDate
								? `A: ${changeDateFormat(
										stepInfo?.currentStep?.actualEndDate,
										'YYYY-MM-DD',
										'DD MMM'
									)}`
								: ''}
							{/* A task finished on its planned date has no days to count, so the zero is
							    left off rather than printed next to the date. */}
							{Math.abs(stepInfo?.currentStep?.dateOver) > 0 && (
								<>
									&nbsp;
									{stepInfo?.currentStep?.formattedTaskStatus === 'LATE_APPROVED' ? (
										<span className="red">
											+{Math.abs(stepInfo?.currentStep?.dateOver)}
										</span>
									) : (
										`+${Math.abs(stepInfo?.currentStep?.dateOver)}`
									)}
								</>
							)}
						</p>
					)}

					{stepInfo?.currentStep?.endDate && (
						<p className="regular-12 gray_dark_02">
							{stepInfo?.currentStep?.endDate
								? `P: ${changeDateFormat(
										stepInfo?.currentStep?.endDate,
										'YYYY-MM-DD',
										'DD MMM'
									)}`
								: ''}
						</p>
					)}

					{deliverableList.includes(stepInfo?.id) && (
						<span className="comment-icon">
							<CommentIcon />
						</span>
					)}
				</td>
			);
		}
	};

	const checkActiveColumn = (item) => {
		return (
			orderDashboardStore.isAllColumnVisible ||
			(orderDashboardStore.isCriticalColumnVisible && item.isCritical) ||
			(orderDashboardStore.isSweaterColumnVisible && item.applicableFabric === 'SWEATER') ||
			(orderDashboardStore.isProductionColumnVisible && item?.stageConstants === 'PRODUCTION') ||
			(orderDashboardStore.isSampleColumnVisible && item?.stageConstants === 'SAMPLING') ||
			item.isColumnVisible
		);
	};

	// The Inspection and Delivery columns pinned to the right of the table. All task covers them
	// along with every other task, and their own switches in the column dropdown still turn them
	// on by themselves. The heading and the cells read this same answer, or the table stops
	// lining up.
	const checkActiveTrailingColumn = (heading) => {
		const stage = heading?.stageConstants;
		if (stage !== 'DELIVERY' && stage !== 'INSPECTION') return false;
		if (orderDashboardStore.isAllColumnVisible) return true;
		return stage === 'DELIVERY'
			? orderDashboardStore.isDeliveryColumnVisible
			: orderDashboardStore.isInspectionColumnVisible;
	};

	const renderSteps = (tnaStatus, style, rfqId) => {
		return orderDashboardData.map((item) => {
			if (checkActiveColumn(item)) {
				return renderStepsTableDetails(item, tnaStatus, style, 1, rfqId);
			}
		});
	};

	const renderEmptySteps = () => {
		return orderDashboardData.map((item, index, isSet) => {
			if (checkActiveColumn(item)) {
				return <td key={`steps-${index}-${isSet}`}>N/A</td>;
			}
		});
	};

	const closeAllTaskModal = () => {
		setHiddenUnsafeTasks([]);
		setSelectedItem({});
		setShowAllTask(false);
	};

	const handleShowInspectionDetails = (item) => {
		setSelectedInspectionItem(item);
		setShowInspectionDetails(true);
	};

	const closeInspectionDetailsModal = () => {
		setSelectedInspectionItem(null);
		setShowInspectionDetails(false);
	};

	const isTnaResponseHasLength = (tnaResponseArray = []) => {
		return tnaResponseArray?.length > 0;
	};

	const renderTableBody = (items) => {
		const groupedDataMap = new Map();
		const orderTracker = [];

		items.forEach((item) => {
			const { productId, setMemberIds = [], ...rest } = item;

			if (!groupedDataMap.has(productId)) {
				groupedDataMap.set(productId, {
					productId,
					...rest,
					setMemberIds: [...new Set(setMemberIds)],
					etd: []
				});

				orderTracker.push(productId);
			}

			groupedDataMap.get(productId).etd.push({
				productId,
				...rest,
				setMemberIds: [...new Set(setMemberIds)]
			});
		});

		const result = orderTracker.map((productId) => groupedDataMap.get(productId));

		const renderBody = (key, item, isProductOfSet, idx, rowSpanCount, orderInfoMap, tnaResponse) => {
			let selectedOrderInfo = orderInfoMap?.[`${item.id}_${idx}`];
			let isKeyExist = `${item.id}_${idx}` in orderInfoMap;
			let rfqId = orderInfoMap[`${item.id}_${idx}`]?.id;
			return (
				<tr key={key} className={item?.tnaNotCreated ? 'tna-not-created-row' : ''}>
					{idx === 0 &&
						orderDashboardStore?.isBrandColumnVisible &&
						requestFrom !== 'ORDER_DETAILS' && (
							<BrandAndTeamCell rowSpan={rowSpanCount} item={item} />
						)}
					{idx === 0 && orderDashboardStore?.isStyleColumnVisible && (
						<StyleCell
							isProductOfSet={isProductOfSet}
							rowSpan={rowSpanCount}
							item={item}
							openDesignViewModal={openDesignViewModal}
							requestFrom={requestFrom}
						/>
					)}
					{idx === 0 &&
						orderDashboardStore?.isFabricColumnVisible &&
						requestFrom !== 'ORDER_DETAILS' && <FabricCell rowSpan={rowSpanCount} item={item} />}
					{orderDashboardStore?.isETDColumnVisible && isKeyExist && (
						<BuyerEtdCell
							index={selectedOrderInfo?.idx}
							date={selectedOrderInfo?.buyerEtd}
							rowSpan={selectedOrderInfo?.rowSpanCount}
						/>
					)}
					{orderDashboardStore?.isETDColumnVisible && isKeyExist && (
						<FactoryEtdCell
							date={selectedOrderInfo?.factoryEtd}
							rowSpan={selectedOrderInfo?.rowSpanCount}
						/>
					)}
					{isKeyExist && orderDashboardStore?.isPOColumnVisible && (
						<PoCell
							rowSpan={selectedOrderInfo?.rowSpanCount}
							item={selectedOrderInfo}
							streamList={streamList}
						/>
					)}

					{isKeyExist && orderDashboardStore?.isPriceColumnVisible && (
						<QuotationBuyerPriceCell
							rowSpan={selectedOrderInfo?.rowSpanCount}
							item={selectedOrderInfo}
						/>
					)}
					{isKeyExist && orderDashboardStore?.isPriceColumnVisible && (
						<QuotationFactoryPriceCell
							rowSpan={selectedOrderInfo?.rowSpanCount}
							item={selectedOrderInfo}
						/>
					)}
					{isKeyExist && orderDashboardStore?.isSCColumnVisible && (
						<ScCell rowSpan={selectedOrderInfo?.rowSpanCount} item={item} />
					)}

					{isShowTasks && (
						<>
							{isTnaResponseHasLength(isProductOfSet ? tnaResponse : tnaResponse[0])
								? renderSteps(
										isProductOfSet ? tnaResponse : tnaResponse[0],
										selectedOrderInfo ?? item,
										rfqId
									)
								: renderEmptySteps(item, isProductOfSet)}
						</>
					)}

					{isKeyExist &&
						placeOrderDeleveryInspactionColumn.map((lastTwoHeading) => {
							if (checkActiveTrailingColumn(lastTwoHeading)) {
								return renderStepsTableDetails(
									lastTwoHeading,
									selectedOrderInfo?.tnaResponse,
									selectedOrderInfo ?? item,
									selectedOrderInfo?.rowSpanCount,
									rfqId
								);
							}
						})}

					{isKeyExist && orderDashboardStore?.isInspectionDetailsColumnVisible && (
						<InspectionDetailsCell
							rowSpan={selectedOrderInfo?.rowSpanCount}
							item={selectedOrderInfo}
							onClick={() => handleShowInspectionDetails(selectedOrderInfo)}
						/>
					)}
					{isKeyExist && orderDashboardStore?.isExFactoryColumnVisible && (
						<ExFactoryCell rowSpan={selectedOrderInfo?.rowSpanCount} item={selectedOrderInfo} />
					)}
					{isKeyExist && orderDashboardStore?.isSPColumnVisible && (
						<ShipmentCell rowSpan={selectedOrderInfo?.rowSpanCount} item={selectedOrderInfo} />
					)}
					{isKeyExist && isShowRemarks && (
						<td rowSpan={selectedOrderInfo?.rowSpanCount}>
							<OrderRemarks id={selectedOrderInfo.id} message={selectedOrderInfo.remarks} />
						</td>
					)}
				</tr>
			);
		};

		return result?.map((order) => {
			const isProductOfSet = order?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT;
			let tnaResponseList = [];
			let orderInfoMap = {};
			let etdInterVal = 0;

			order?.etd.forEach((item, idx) => {
				const setMemberResponseCount = Array.isArray(item.tnaSetMemberResponseList)
					? item.tnaSetMemberResponseList.length
					: 0;
				const rowSpanCountForOrderInfo = isProductOfSet ? Math.max(setMemberResponseCount, 1) : 1;

				if (isProductOfSet) {
					if (setMemberResponseCount > 0) {
						tnaResponseList.push(...item.tnaSetMemberResponseList);
					} else {
						// Fallback to render a single row when set member responses are missing.
						tnaResponseList.push(item.tnaResponse || []);
					}
				} else {
					tnaResponseList.push([item.tnaResponse]);
				}

				orderInfoMap[`${order.id}_${etdInterVal}`] = {
					id: item.id,
					productReferenceNo: item.productReferenceNo,
					productFeatureImageUrl: item.productFeatureImageUrl,
					remarks: item.remarks,
					commercialInvoiceAmount: item.commercialInvoiceAmount,
					commercialInvoiceDate: item.commercialInvoiceDate,
					commercialInvoiceNo: item.commercialInvoiceNo,
					shippedQuantity: item.shippedQuantity,
					shipmentQuantityInPcs: item.shipmentQuantityInPcs,
					factoryName: item.factoryName,
					salesContractRefNo: item.salesContractRefNo,
					salesContractAmount: item.salesContractAmount,
					amendmentNo: item.amendmentNo,
					orderId: item.orderId,
					orderName: item.orderName,
					orderRefNo: item.orderRefNo,
					poQuantity: item.poQuantity,
					poQuantityInPcs: item.poQuantityInPcs,
					poValue: item.poValue,
					poReceivedDate: item.poReceivedDate,
					margin: item.margin,
					marginPercent: item.marginPercent,
					stream: item.stream,
					buyerEtd: item.buyerEtd,
					factoryEtd: item.factoryEtd,
					tnaStatus: item.tnaStatus,
					tnaStatusCountMap: item.tnaStatusCountMap,
					tnaResponse: item.tnaResponse,
					exFactoryDate: item.exFactoryDate,
					exFactoryQuantity: item.exFactoryQuantity,
					exFactoryAmount: item.exFactoryAmount,
					inspectionSummary: item.inspectionSummary,
					skuList: item.skuList,
					poNumber: item.poNumber,
					buyerQuotationType: item.buyerQuotationType,
					factoryQuotationType: item.factoryQuotationType,
					designWiseBuyerPrice: item.designWiseBuyerPrice,
					designWiseFactoryPrice: item.designWiseFactoryPrice,
					sizeWiseBuyerPriceResponse: item.sizeWiseBuyerPriceResponse,
					sizeWiseFactoryPriceResponse: item.sizeWiseFactoryPriceResponse,
					colorWiseBuyerPriceResponse: item.colorWiseBuyerPriceResponse,
					colorWiseFactoryPriceResponse: item.colorWiseFactoryPriceResponse,
					idx,
					rowSpanCount: rowSpanCountForOrderInfo
				};

				etdInterVal += rowSpanCountForOrderInfo;
			});

			return tnaResponseList?.map((tnaResponse, idx) =>
				renderBody(
					`order_dashboard_${order.id}_${idx}`,
					order,
					isProductOfSet,
					idx,
					tnaResponseList.length,
					orderInfoMap,
					tnaResponse
				)
			);
		});
	};

	const handleHorizontalScroll = (element, speed, distance, step) => {
		let scrollAmount = 0;
		const slideTimer = setInterval(() => {
			element.scrollLeft += step;
			scrollAmount += Math.abs(step);
			if (scrollAmount >= distance) {
				clearInterval(slideTimer);
			}
			if (element.scrollLeft === 0) {
				setArrowDisable(true);
			} else {
				setArrowDisable(false);
			}
		}, speed);
	};

	const openDesignViewModal = (design) => {
		dispatch(
			setDesignViewModalInfo({
				isShowMergeTechPack:
					design?.isSet || design.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT,
				id: design?.productId,
				onCloseViewModal: () => {
					dispatch(clearDesignViewModalInfo({}));
				},
				source: 'ORDER_DETAILS',
				orderId: design?.orderId
			})
		);
	};

	// A style that has not been placed with a factory yet still belongs somewhere, so it is
	// grouped under the name the server counts those styles under rather than dropped.
	const factoryGroupName = (item) => item?.factoryName || 'No factory';

	const groupWiseOrderList = (data, groupByCount) => {
		if (
			selectedGroupBy === ['TNA_STATUS', 'BRAND', 'ORDER'] &&
			(!Array.isArray(data) || data.length === 0)
		) {
			return [];
		}

		if (
			selectedGroupBy === ['TNA_STATUS', 'BRAND', 'ORDER'] &&
			(!Array.isArray(groupByCount) || groupByCount.length === 0)
		) {
			return [];
		}

		// Only the order grouping has an id to key on; the rest are named by the group itself,
		// and a factory group is named by the server so both ends agree on what a group is.
		const groupWiseMap = groupByCount.reduce((acc, item) => {
			if (selectedGroupBy === 'ORDER') {
				acc[item.id] = item.name;
			} else {
				acc[item.name] = item.name;
			}

			return acc;
		}, {});

		const groupedList = data.reduce((acc, item) => {
			let key;
			if (selectedGroupBy === 'TNA_STATUS') {
				key = groupWiseMap[item.tnaStatus];
			} else if (selectedGroupBy === 'BRAND') {
				key = groupWiseMap[item.brandName];
			} else if (selectedGroupBy === 'ORDER') {
				key = groupWiseMap[item.orderId];
			} else if (selectedGroupBy === 'FACTORY') {
				key = groupWiseMap[factoryGroupName(item)];
			} else if (selectedGroupBy === 'BRAND_FACTORY') {
				key = groupWiseMap[`${item.brandName} - ${factoryGroupName(item)}`];
			} else {
				key = 'Ungrouped';
			}
			if (key) {
				(acc[key] = acc[key] || []).push(item);
			}
			return acc;
		}, {});

		return Object.entries(groupedList);
	};

	const calculateExpectedMargin = (findGroupByCount) => {
		return parseFloat(findGroupByCount?.amount) - parseFloat(findGroupByCount?.productionCost);
	};

	const renderCommonTableHeadings = (showGroupWiseTitle = true, findGroupByCount, groupKey, items) => {
		let groupHeading = document.getElementById('table-hading-group');
		let margin = calculateExpectedMargin(findGroupByCount).toFixed(2);
		return (
			<thead>
				{showGroupWiseTitle && (
					<tr className="summary-row">
						{groupHeading?.childElementCount && (
							<td colSpan={groupHeading?.childElementCount} className="bg-gray">
								<h3 className="semibold-16">
									{capitalizeFirstLetter(groupKey).split('_').join(' ')}
									{findGroupByCount?.styleCount &&
										` (${findGroupByCount?.styleCount} Styles / $${formatNumberWithSuffix(
											findGroupByCount?.amount
										)} ${!isNaN(margin) ? `- Expected Margin $${formatNumberWithSuffix(margin)}` : ''})`}
								</h3>
							</td>
						)}
					</tr>
				)}
				<tr id="table-hading-group" className="heading-row">
					{orderDashboardStore?.isBrandColumnVisible && requestFrom !== 'ORDER_DETAILS' && (
						<th className="brand-and-team">Brand &amp; Team</th>
					)}
					{orderDashboardStore?.isStyleColumnVisible && <th className="style-column">Style</th>}
					{orderDashboardStore?.isFabricColumnVisible && requestFrom !== 'ORDER_DETAILS' && (
						<th>Fabric</th>
					)}
					{orderDashboardStore?.isETDColumnVisible && (
						<th className="etd-column buyer-etd">Buyer ETD</th>
					)}
					{orderDashboardStore?.isETDColumnVisible && (
						<th className="etd-column factory-etd">Factory ETD</th>
					)}
					{orderDashboardStore?.isPOColumnVisible && <th>PO details</th>}
					{orderDashboardStore?.isPriceColumnVisible && <th>Buyer Price</th>}
					{orderDashboardStore?.isPriceColumnVisible && <th>Factory Price</th>}
					{orderDashboardStore?.isSCColumnVisible && <th className="sc-details">SC details</th>}
					{isShowTasks && renderTableHeading(items)}
					{placeOrderDeleveryInspactionColumn.map((lastTwoHeading, index) => {
						if (checkActiveTrailingColumn(lastTwoHeading)) {
							return (
								<th key={`${lastTwoHeading?.name}-${index}`}>
									<div className="d-flex align-items-center align-self-center">
										{lastTwoHeading?.stageConstants === 'INSPECTION' &&
											renderAllCurrentStepCheckbox(lastTwoHeading, true)}
										{lastTwoHeading?.name}
									</div>
								</th>
							);
						}
					})}
					{orderDashboardStore?.isInspectionDetailsColumnVisible && (
						<th className="inspection-details">Inspection Details</th>
					)}
					{orderDashboardStore?.isExFactoryColumnVisible && <th>Ex-factory Details</th>}
					{orderDashboardStore?.isSPColumnVisible && <th>Shipment Details</th>}
					{isShowRemarks && <th>Remarks</th>}
				</tr>
			</thead>
		);
	};

	const renderGroupWiseTable = () => {
		const groupedOrders = groupWiseOrderList(orderStore, groupByCount);

		return (
			<>
				{groupedOrders.map(([groupKey, items], index) => {
					const findGroupByCount = groupByCount.find((group) => group?.name == groupKey);
					return (
						<React.Fragment key={`${groupKey}-${index}`}>
							{renderCommonTableHeadings(
								groupKey !== 'Ungrouped',
								findGroupByCount,
								groupKey,
								items
							)}
							<tbody>{renderTableBody(items)}</tbody>
						</React.Fragment>
					);
				})}
			</>
		);
	};

	const renderTable = () => {
		return (
			<React.Fragment>
				{renderCommonTableHeadings(false)}
				<tbody>{renderTableBody(orderStore)}</tbody>
			</React.Fragment>
		);
	};

	const completeSelectedTasks = async (tasks, date = null) => {
		try {
			dispatch(openLoader());
			const data = await completeOrderStepByIdList(
				tasks.map((task) => task.id),
				date
			);
			updateOrderStepByOrders(data);
			setSelectedTasks([]);
			setCompletionDate(null);
			dispatch(closeLoader());
		} catch (e) {
			printApiError(e);
			dispatch(closeLoader());
		}
	};

	// Handing over to the forwarder records that style's shipped quantity and ex-factory price, so
	// it asks for those first - once per style that has one selected - and only then is the whole
	// selection completed. Rating the supplier stays where it is, inside that same modal.
	const handleTaskComplete = async (date = null) => {
		setShowPastDateModal(false);
		setCompletionDate(date);
		const handoverTasks = selectedTasks.filter((task) => task.stepName === HANDOVER_TO_FORWARDER_STEP);
		if (handoverTasks.length > 0) {
			setHandoverTask(handoverTasks[0]);
			setPendingHandoverTasks(handoverTasks.slice(1));
			return;
		}
		await completeSelectedTasks(selectedTasks, date);
	};

	const onHandoverDetailsSubmitted = async () => {
		const [nextHandoverTask, ...remaining] = pendingHandoverTasks;
		setHandoverTask(nextHandoverTask ?? null);
		setPendingHandoverTasks(remaining);
		if (!nextHandoverTask) await completeSelectedTasks(selectedTasks, completionDate);
	};

	// Backing out of the shipment details leaves the selection as it was and completes nothing:
	// the handover cannot be signed off without them, and the rest was picked to go with it.
	const onHandoverDetailsCancelled = () => {
		setHandoverTask(null);
		setPendingHandoverTasks([]);
		setCompletionDate(null);
	};

	return (
		<LoaderComponent loading={loader}>
			{showSummary && <OrderSummary />}
			<div className="scroll-table-buttons">
				<button
					className="left"
					onClick={() => {
						handleHorizontalScroll(elementRef.current, 25, 100, -400);
					}}
					disabled={arrowDisable}
				>
					<SlideIcon />
				</button>
				&nbsp;&nbsp;
				<button
					className="right"
					onClick={() => {
						handleHorizontalScroll(elementRef.current, 25, 100, 400);
					}}
				>
					<SlideIcon />
				</button>
			</div>
			<div className="order-dashboard">
				<table
					id="orderDashboardTable"
					className={
						showSummary
							? `table with-border order-dashboard-table scroll-x-label scroll-y-label`
							: `table with-border order-dashboard-table hide-summary scroll-x-label scroll-y-label`
					}
					ref={elementRef}
					onScroll={handleOrderDashboardScroll}
				>
					{activePage === 'ORDER_DASHBOARD' ? renderGroupWiseTable() : renderTable()}
				</table>
			</div>
			<Modal
				show={showAllTask}
				onHide={closeAllTaskModal}
				className="modal-right task-conversation with-tag-person"
				aria-labelledby="example-custom-modal-styling-title"
			>
				<AllTask
					closeModal={closeAllTaskModal}
					hiddenTasks={hiddenUnsafeTasks}
					styleInfo={selectedItem}
					callback={() => callback()}
					isSetProduct={isTnaStatusModalHasSet}
				/>
			</Modal>
			<InspectionDetailsModal
				show={showInspectionDetails}
				onHide={closeInspectionDetailsModal}
				item={selectedInspectionItem}
			/>
			{handoverTask !== null && (
				<ExFactorySupplierQuote
					rfqId={handoverTask.rfqId}
					onCloseModal={onHandoverDetailsCancelled}
					onSuccess={onHandoverDetailsSubmitted}
					supplierDetails={{
						supplierName: handoverTask.factoryName,
						supplierId: handoverTask.factoryId
					}}
				/>
			)}
			{selectedTasks.length > 0 && (
				<div className="design-count-section bg-white order-task-popup d-flex justify-content-between align-items-center">
					<div className="d-flex align-items-center">
						<p
							className="regular-14 weight-500 cursor-pointer mb-0"
							onClick={() => handleTaskComplete()}
						>
							Complete {selectedTasks.length} task
						</p>
						{/* Signing off work that was finished days ago: the same completion, dated the
						    day it actually happened rather than today. */}
						<p
							className="regular-14 weight-500 cursor-pointer mb-0 ml-4 blue_dark_01"
							onClick={() => setShowPastDateModal(true)}
						>
							Complete on a past date
						</p>
					</div>
					<CommonImage
						src="/icons/close.svg"
						alt="down"
						className="ml-1 close-icon cursor-pointer"
						onClick={() => setSelectedTasks([])}
					/>
				</div>
			)}
			<CompleteOnPastDateModal
				show={showPastDateModal}
				onHide={() => setShowPastDateModal(false)}
				taskCount={selectedTasks.length}
				handoverDate={latestHandoverDate}
				onConfirm={handleTaskComplete}
			/>
		</LoaderComponent>
	);
};

export default OrderDashboard;
